import * as React from "react";

import {
  concatStyleSets,
  IButtonStyles,
  ITheme,
  memoizeFunction,
  mergeStyles,
} from "@fluentui/react";

import {
  ActionControlSemanticColors,
  AppColors,
  buttonBorderWidth,
  buttonLabelHorizontalMargin,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  getActionControlSemanticColorsWithTheme,
  getBaseButtonStyles,
  getPrimaryControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

import { IPivotHeaderStyles } from "./PivotHeader.Props";

const pivotHeaderHorizontalPadding: number = 10;

export const pivotHeaderLabelClassname = "pivot-header-label";
export const pivotHeaderClassName = "pivot-header-root";

export const getPivotHeaderButtonStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<IPivotHeaderStyles>
  ): IButtonStyles => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const baseButtonStyles: IButtonStyles = getBaseButtonStyles(theme);
    const themingEnabled = isThemingEnabled();
    const defaultPivotHeaderStyles: IButtonStyles = {
      root: {
        height: 40,
        backgroundColor: actionControlSemanticColors.defaultBackgroundColor,
        color: themingEnabled ? theme.palette.black : "#000000",
        padding: `0 ${pivotHeaderHorizontalPadding}px`,
        selectors: {
          ":hover": {
            color: themingEnabled
              ? actionControlSemanticColors.textColor
              : "#212121",
          },
        },
      },
      rootHovered: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootFocused: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootPressed: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      label: [pivotHeaderLabelClassname, { display: "block" }],
      icon: {
        width: 20,
        height: 20,
      },
    };

    const customButtonStyles = customStyles && customStyles.pivotHeader;

    return concatStyleSets(
      baseButtonStyles,
      defaultPivotHeaderStyles,
      customButtonStyles
    );
  }
);

export const getSelectedPivotHeaderButtonStyles = memoizeFunction(
  (
    theme: ITheme,
    appColors?: AppColors,
    customStyles?: Partial<IPivotHeaderStyles>
  ): IButtonStyles => {
    const defaultPivotHeaderStyles = getPivotHeaderButtonStyles(
      theme,
      customStyles
    );
    const primarySemanticColors = getPrimaryControlSemanticColorsWithTheme(
      theme,
      appColors
    );
    const defaultSelectedPivotHeaderStyles: IButtonStyles = {
      root: [
        {
          // VSTS:2734821: We currently don't pass in AppColors in blue chicken for TaskPanes. Waiting for AppContext change
          // to include AppColors so we don't have to pass everything from WordTaskPaneGroup
          backgroundColor: isThemingEnabled()
            ? primarySemanticColors.primaryDefaultBackgroundColor
            : "#2b579a",
          color:
            primarySemanticColors.defaultTextLabelColorForPrimaryBackground,
          selectors: {
            ":hover": {
              color:
                primarySemanticColors.defaultTextLabelColorForPrimaryBackground,
            },
          },
        },
        commonHighContrastOutlineToggledOn,
        commonHighContrastHoverFocusStyles,
      ],
      rootHovered: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootFocused: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootPressed: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      label: [pivotHeaderLabelClassname, { fontWeight: 600, display: "block" }],
    };

    const customButtonStyles = customStyles && customStyles.selectedPivotHeader;

    return concatStyleSets(
      defaultPivotHeaderStyles,
      defaultSelectedPivotHeaderStyles,
      customButtonStyles
    );
  }
);

/* eslint-disable @typescript-eslint/naming-convention */
// tslint:disable-next-line:interface-name
export interface IPivotHeaderClassNames {
  /* eslint-enable @typescript-eslint/naming-convention */
  wrapper: string;
}

export const getClassNames = memoizeFunction(
  (styles?: Partial<IPivotHeaderStyles>): IPivotHeaderClassNames => {
    return {
      wrapper: styles ? mergeStyles(styles.wrapper, pivotHeaderClassName) : "",
    };
  }
);

export const pivotWrapperStyles: React.CSSProperties = {
  textAlign: "center",
  position: "relative",
};

export const getDefaultGhostPivotLabelStyles = (
  customStyles?: React.CSSProperties
): React.CSSProperties => {
  return {
    display: "block",
    visibility: "hidden",
    fontWeight: 600,
    height: 0,
    // The padding values account for the border, padding, and margin around the pivot header label.
    paddingLeft:
      pivotHeaderHorizontalPadding +
      buttonLabelHorizontalMargin +
      buttonBorderWidth,
    paddingRight:
      pivotHeaderHorizontalPadding +
      buttonLabelHorizontalMargin +
      buttonBorderWidth,
    ...customStyles,
  };
};

export const getVerticalSeparatorClassNames = memoizeFunction((): string =>
  mergeStyles({
    display: "inline-block",
    width: 4,
  })
);
